import React, { useState, useEffect, useRef } from 'react';
import { X, Mail, Paperclip, PlayCircle, File, Edit3, Eye, ChevronRight, AlertTriangle } from 'react-feather';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { integrationsAPI } from '../services/integrationsAPI';
import { mediaUploadService } from '../services/mediaUploadService';
import { userAPI, familyAPI, athleteAPI, athleteSharingAPI } from '../services/api';
import { useConfirm } from '../hooks/useConfirm';
import Modal, { ModalHandle } from './Modal';
import YouTubeVideoPickerModal from './YouTubeVideoPickerModal';
import TemplatePickerModal from './TemplatePickerModal';
import { useEmailVariables } from '../hooks/useEmailVariables';
import { QuickTags } from './QuickTags';
import { QuickTag, resolveEmailVariables, EventScheduleGame } from '../utils/emailVariables';
import { useResponsive } from '../hooks/useResponsive';
import { Select, TextInput, TextArea } from './ui';

// Strip any merge tags the resolver couldn't fill, plus the now-empty "Label:"
// lines they leave behind (e.g. an empty "GPA:") so coaches never receive
// literal {{...}} or dangling labels (#199). Mirrors EmailCoachModal's
// send-time strip.
const stripUnresolvedTags = (text: string): string =>
  text
    .replace(/\{\{\s*[^}]*?\s*\}\}/g, '')
    .replace(/^[ \t]*[A-Za-z][A-Za-z .]*:[ \t]*$/gm, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

interface Coach {
  coach_name: string;
  email: string;
  title?: string;
  school?: string;
  schoolLogo?: string;
  coach_id?: string;
  school_id?: string;
}

interface BulkEmailCoachModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Fires after onClose, only when every coach was emailed. Not after a partial send. */
  onSent?: () => void;
  coaches: Coach[];
  athleteName?: string;
  onRemoveCoach?: (coachEmail: string) => void;
  /**
   * KGH-795 item 11. The athlete's games at the event this send is about, when
   * the caller opened the composer from one (the tournament's "send my
   * schedule"). It feeds the {{athlete.tournamentSchedule}} tag: offered in the
   * tag rail here, resolved per coach at send. Absent everywhere else, and the
   * tag is then neither offered nor resolvable — same as any tag with no data.
   */
  eventSchedule?: EventScheduleGame[] | null;
  /**
   * WHICH athlete this send is about, when the caller knows.
   *
   * Without it this composer resolves the athlete from the SIGNED-IN USER's own
   * family_members.athlete_profile_id, which is one athlete per user. A guardian
   * with two players therefore got whichever one is denormalised on their row —
   * fine when the composer is opened from a generic "email these coaches"
   * button, wrong the moment the send is about a specific player. The tournament
   * surface knows exactly whose event it is (it already charges that athlete's
   * §1.5 unlock), so it says so, and the draft, the merge tags, the share link
   * and the schedule block all describe the same child.
   */
  athleteProfileId?: string | null;
  /**
   * A draft the caller has already written for this specific send. When given,
   * it replaces the generic introduction template, so a purpose-built entry
   * point ("email this program my schedule") opens on a message that is ready
   * to read and send rather than one the athlete has to rewrite. Still fully
   * editable — it is a starting point, not a locked body.
   */
  initialSubject?: string;
  initialBody?: string;
}

interface AthleteProfile {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  dateOfBirth?: string;
  graduationYear?: number;
  city?: string;
  state?: string;
  sport?: string;
  grade?: number;
  position?: string;
  createdBy?: string;
  createdAt?: string;
  youtube_channel_url?: string;
  youtube_channel_handle?: string;
}

interface AcademicData {
  satMath?: string;
  satReadingWriting?: string;
  satTotal?: string;
  actTotal?: string;
  gpaScale?: string;
  currentGpa?: string;
  psatTotal?: string;
  psatMath?: string;
  psatReadingWriting?: string;
}

interface PerformanceMetrics {
  sixty_yard_time?: number | null;
  forty_yard_time?: number | null;
  twenty_yard_time?: number | null;
  ten_yard_time?: number | null;
  home_to_first?: number | null;
  pop_time?: number | null;
  exit_velocity?: number | null;
  overhand_velocity?: number | null;
  grip_strength?: number | null;
}

interface SportInfo {
  sport?: string;
  positions?: string[];
  bats?: string;
  throws?: string;
  height?: string | number;
  weight?: number;
}

interface SchoolInfo {
  school?: {
    name?: string;
  };
  travelClub?: {
    name?: string;
  };
}

const BulkEmailCoachModal: React.FC<BulkEmailCoachModalProps> = ({
  isOpen,
  onClose,
  onSent,
  coaches,
  athleteName = 'Athlete',
  onRemoveCoach,
  eventSchedule,
  athleteProfileId,
  initialSubject,
  initialBody,
}) => {
  const { sessionToken } = useAuth();
  const toast = useToast();
  const { isMobile } = useResponsive();
  const [athleteId, setAthleteId] = useState<string>('');
  const [athleteProfile, setAthleteProfile] = useState<AthleteProfile | null>(null);
  const [academicData, setAcademicData] = useState<AcademicData | null>(null);
  const [performanceMetrics, setPerformanceMetrics] = useState<PerformanceMetrics | null>(null);
  const [sportInfo, setSportInfo] = useState<SportInfo | null>(null);
  const [schoolInfo, setSchoolInfo] = useState<SchoolInfo | null>(null);
  // Public profile share link — only set when sharing is on; drives the
  // {{athlete.profileShareLink}} tag + hint, and is passed into every
  // per-coach resolveEmailVariables() call below.
  const [profileShareUrl, setProfileShareUrl] = useState<string | undefined>(undefined);
  const [isSending, setIsSending] = useState(false);
  const [sentCount, setSentCount] = useState(0);
  const [isGmailConnected, setIsGmailConnected] = useState(false);
  const [isCheckingConnection, setIsCheckingConnection] = useState(true);
  // Coaches successfully emailed this session (keyed by email) — a retry after
  // a partial failure prunes these so no one is double-sent (#191).
  const [sentCoachKeys, setSentCoachKeys] = useState<Set<string>>(new Set());
  // Coaches that failed to send, surfaced to the user so a retry is meaningful.
  const [failedRecipients, setFailedRecipients] = useState<{ name: string; school?: string }[]>([]);
  
  const [subject, setSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [attachedMedia, setAttachedMedia] = useState<File[]>([]);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const [isUploadingAttachment, setIsUploadingAttachment] = useState(false);
  const [youtubePickerVisible, setYoutubePickerVisible] = useState(false);
  const [templatePickerVisible, setTemplatePickerVisible] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [previewCoachIndex, setPreviewCoachIndex] = useState(0);
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [ccEmails, setCcEmails] = useState('');
  const [bccEmails, setBccEmails] = useState('');

  const fileInputRef = useRef<HTMLInputElement>(null);

  const { ConfirmComponent: RemoveCoachConfirm, confirm: confirmRemoveCoach } = useConfirm({
    title: 'Remove Coach',
    message: 'Are you sure you want to remove this coach from this message?',
    confirmText: 'Remove',
    cancelText: 'Cancel',
    variant: 'warning',
  });

  // Use shared email variables system
  const { tags } = useEmailVariables({
    athlete: athleteProfile,
    coach: coaches[0], // Use first coach for template
    sportInfo,
    academicData,
    performanceMetrics,
    schoolInfo,
    profileShareUrl,
    eventSchedule,
    context: {
      hasCoach: true,
      hasYouTube: !!athleteProfile?.youtube_channel_url,
      isThreadReply: false
    }
  });

  // Load athlete data
  useEffect(() => {
    if (isOpen && sessionToken) {
      // Clear stale data first, then load fresh data
      setAthleteProfile(null);
      setAthleteId('');
      setAcademicData(null);
      setPerformanceMetrics(null);
      setSportInfo(null);
      setSchoolInfo(null);
      setProfileShareUrl(undefined);
      setSentCoachKeys(new Set());
      setFailedRecipients([]);
      loadAthleteData();
      checkGmailConnection();
    } else if (!isOpen) {
      // Clear cached athlete data when modal closes to prevent stale data
      setAthleteProfile(null);
      setAthleteId('');
      setAcademicData(null);
      setPerformanceMetrics(null);
      setSportInfo(null);
      setSchoolInfo(null);
      setProfileShareUrl(undefined);
    }
  }, [isOpen, sessionToken, athleteProfileId]);

  // Fetch the athlete's public profile share URL. Resolves to a value only
  // when sharing is enabled; otherwise stays undefined (tag not offered, hint
  // shown, and every per-coach resolve leaves the tag blank — same as before).
  const loadProfileShareUrl = async (forAthleteId: string) => {
    if (!sessionToken) return;
    try {
      const res = await athleteSharingAPI.getShareSettings(sessionToken, forAthleteId);
      if (res?.data?.shareUrl) setProfileShareUrl(res.data.shareUrl);
    } catch {
      /* sharing not enabled or fetch failed — tag stays unavailable */
    }
  };

  // Generate email template when modal opens
  useEffect(() => {
    if (isOpen && !isCheckingConnection && isGmailConnected && athleteProfile && !subject && !emailBody) {
      generateEmailTemplate();
    }
  }, [isOpen, isCheckingConnection, isGmailConnected, athleteProfile]);

  const loadAthleteData = async () => {
    if (!sessionToken) return;

    try {
      const userResult = await userAPI.getProfile(sessionToken);
      const familyResult = await familyAPI.getMyFamily(sessionToken);

      if (familyResult.family?.members) {
        const familyMember = familyResult.family.members.find(
          (member: any) => member.id === userResult.id
        );

        /*
         * The caller's athlete wins. family_members.athlete_profile_id is ONE
         * athlete per user, so a guardian with two players had every draft
         * describe whichever one is denormalised on their own row - including
         * the tournament schedule block, which would then be the other child's
         * games. A surface that knows whose send this is passes athleteProfileId
         * and the whole draft follows it. Everywhere else is unchanged.
         */
        const subjectAthleteId = athleteProfileId || familyMember?.athlete_profile_id;

        if (subjectAthleteId) {
          setAthleteId(subjectAthleteId);
          loadProfileShareUrl(subjectAthleteId);

          const profileData = await userAPI.getComprehensiveProfile(sessionToken);
          if (profileData.athletes && profileData.athletes.length > 0) {
            const athlete = profileData.athletes.find(
              (a: any) => a.id === subjectAthleteId
            );
            if (athlete) {
              setAthleteProfile({
                id: athlete.id,
                firstName: athlete.firstName,
                lastName: athlete.lastName,
                email: athlete.email,
                phone: athlete.phone,
                dateOfBirth: athlete.dateOfBirth,
                graduationYear: athlete.graduationYear,
                city: athlete.city,
                state: athlete.state,
                sport: athlete.sport,
                grade: parseInt(athlete.grade) || undefined,
                position: athlete.position,
                createdBy: athlete.createdBy,
                createdAt: athlete.createdAt,
                youtube_channel_url: (athlete as any).youtube_channel_url,
                youtube_channel_handle: (athlete as any).youtube_channel_handle,
              });

              // Load sport info
              if ((athlete as any).sportInfo) {
                const sportData = (athlete as any).sportInfo;
                setSportInfo({
                  sport: sportData.sport,
                  positions: typeof sportData.positions === 'string' 
                    ? sportData.positions.split(', ').filter((p: string) => p.trim())
                    : Array.isArray(sportData.positions) 
                      ? sportData.positions
                      : [],
                  bats: sportData.bats,
                  throws: sportData.throws,
                  height: sportData.height,
                  weight: sportData.weight,
                });
              }

              // Load school info
              if ((athlete as any).schoolInfo) {
                setSchoolInfo((athlete as any).schoolInfo);
              }

              // Load academic data
              try {
                const academic = await athleteAPI.getAcademicPerformance(
                  sessionToken,
                  subjectAthleteId
                );
                if ((academic as any).success && (academic as any).data?.academicPerformance) {
                  setAcademicData((academic as any).data.academicPerformance);
                }
              } catch (error) {
                console.log('No academic data available');
              }

              // Load performance metrics
              try {
                const metrics = await athleteAPI.getPerformanceMetrics(
                  sessionToken,
                  subjectAthleteId
                );
                if ((metrics as any).success && (metrics as any).data?.metrics) {
                  setPerformanceMetrics((metrics as any).data.metrics);
                }
              } catch (error) {
                console.log('No performance metrics available');
              }
            }
          }
        }
      }
    } catch (error) {
      console.error('Error loading athlete data:', error);
    }
  };

  const checkGmailConnection = async () => {
    if (!sessionToken) return;

    try {
      const userResult = await userAPI.getProfile(sessionToken);
      const familyResult = await familyAPI.getMyFamily(sessionToken);
      
      if (familyResult.family?.members) {
        const familyMember = familyResult.family.members.find(
          (member: any) => member.id === userResult.id
        );
        
        /*
         * The SAME athlete the send will use. sendGmail() at the bottom of this
         * file posts under `athleteId`, and a Gmail connection is stored per
         * athlete profile (integrations.auth.user_id is the athlete id), so
         * checking one athlete's connection and sending as another would show a
         * green banner and then fail at the send. They have to be the same id.
         */
        const subjectAthleteId = athleteProfileId || familyMember?.athlete_profile_id;
        if (subjectAthleteId) {
          const connectionStatus = await integrationsAPI.getConnectionStatus(subjectAthleteId);
          setIsGmailConnected(connectionStatus.gmail);
        }
      }
    } catch (error) {
      console.error('Error checking Gmail connection:', error);
      setIsGmailConnected(false);
    } finally {
      setIsCheckingConnection(false);
    }
  };

  const generateEmailTemplate = () => {
    // A caller-supplied draft wins: it was written for this exact send and
    // already names the occasion, so the athlete reads it instead of rewriting
    // it. Either half can arrive on its own; the other keeps the generic line.
    setSubject(initialSubject ?? 'Ref: {{athlete.name}} {{athlete.graduationYear}} {{athlete.position}}');
    setEmailBody(initialBody ?? (
      `{{coach.name}},\n\n` +
      `My name is {{athlete.name}}. I am a {{athlete.graduationYear}} {{athlete.position}} who currently plays for {{athlete.travelClubName}} and attends {{athlete.schoolName}}. I'm interested in attending {{coach.school}} and wanted to get on your radar.\n\n` +
      `\n\n` +
      `Best regards,\n\n` +
      `{{athlete.name}}\n` +
      `Class of {{athlete.graduationYear}}\n` +
      `GPA: {{athlete.gpa}}\n` +
      `{{athlete.email}}\n` +
      `{{athlete.phone}}`
    ));
  };

  const insertQuickTag = (tag: QuickTag) => {
    setEmailBody(prev => prev + tag.value);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const newFiles = Array.from(files);
    
    // Check total file count
    if (attachedMedia.length + newFiles.length > 4) {
      toast.warning('Maximum 4 files allowed');
      return;
    }

    // Validate each file
    for (const file of newFiles) {
      const fileType = file.type;
      const fileSize = file.size;
      const maxSize = fileType.startsWith('image/') ? 10 * 1024 * 1024 : 
                     fileType.startsWith('video/') ? 100 * 1024 * 1024 : 
                     25 * 1024 * 1024;

      if (fileSize > maxSize) {
        toast.warning(`File ${file.name} exceeds size limit`);
        return;
      }
    }

    setAttachedMedia(prev => [...prev, ...newFiles]);
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveAttachment = (index: number) => {
    setAttachedMedia(prev => prev.filter((_, i) => i !== index));
  };

  const handleRemoveCoach = async (coachEmail: string, coachName: string) => {
    const confirmed = await confirmRemoveCoach();
    if (confirmed && onRemoveCoach) {
      onRemoveCoach(coachEmail);
      if (coaches.length <= 1) {
        toast.info('All coaches removed from this message');
        onClose();
      }
    }
  };

  const handleSendEmail = async () => {
    if (!athleteId) {
      toast.error('Athlete ID not found');
      return;
    }

    if (!isGmailConnected) {
      toast.error('Please connect your Gmail account first');
      return;
    }

    if (!subject.trim() || !emailBody.trim()) {
      toast.warning('Please fill in both subject and message');
      return;
    }

    // Validate Cc/Bcc addresses up front so a malformed token can't be sent.
    const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const badRecipient = [...ccEmails.split(','), ...bccEmails.split(',')]
      .map(e => e.trim())
      .filter(e => e.length > 0)
      .find(e => !EMAIL_RE.test(e));
    if (badRecipient) {
      toast.error(`"${badRecipient}" is not a valid email address. Check your Cc/Bcc.`);
      return;
    }

    if (coaches.length === 0) {
      toast.warning('No coaches selected to email');
      return;
    }

    // Only email coaches we haven't already successfully sent to this session.
    // On a retry after a partial failure this prunes the successful recipients
    // so nobody is double-sent (#191).
    const recipientsToSend = coaches.filter(c => !sentCoachKeys.has(c.email));
    if (recipientsToSend.length === 0) {
      toast.info('Every selected coach has already been emailed.');
      return;
    }

    setIsSending(true);
    setSentCount(0);
    setFailedRecipients([]);

    try {
      let attachmentObjects: { filename: string; url: string; content_type: string; size?: number }[] = [];

      // Upload attachments if any
      if (attachedMedia.length > 0) {
        setIsUploadingAttachment(true);

        const uploadResults = await Promise.all(
          attachedMedia.map(async (file, index) => {
            try {
              const result = await mediaUploadService.uploadMedia(
                {
                  file,
                  type: file.type.startsWith('image/') ? 'image' : file.type.startsWith('video/') ? 'video' : 'document',
                  preview: URL.createObjectURL(file)
                },
                athleteId,
                sessionToken!,
                (progress) => {
                  setUploadProgress(prev => ({
                    ...prev,
                    [`media_${index}`]: progress.percentage
                  }));
                }
              );

              if (result.success && result.fileUrl) {
                return {
                  filename: file.name,
                  url: result.fileUrl,
                  content_type: file.type,
                  size: file.size,
                };
              }
              return null;
            } catch (error) {
              console.error(`Failed to upload ${file.name}:`, error);
              return null;
            }
          })
        );

        setIsUploadingAttachment(false);

        // Block the send if ANY attachment failed to upload — otherwise it is
        // silently dropped and coaches receive an email missing files (#191).
        // Mirrors EmailCoachModal's all-or-nothing upload behavior.
        if (uploadResults.some(result => result === null)) {
          toast.error('One or more attachments failed to upload. No emails were sent — please remove or re-add the file and try again.');
          setIsSending(false);
          return;
        }

        attachmentObjects = uploadResults.filter(Boolean) as typeof attachmentObjects;
      }

      // Send personalized emails sequentially with rate limiting
      const newlySent: string[] = [];
      const failed: { name: string; school?: string }[] = [];

      for (let i = 0; i < recipientsToSend.length; i++) {
        const coach = recipientsToSend[i];

        if (!coach.email) {
          failed.push({ name: coach.coach_name, school: coach.school });
          continue;
        }

        try {
          // Resolve variables for this specific coach, then strip any tags the
          // resolver couldn't fill (and the dangling "GPA:" lines they leave)
          // so coaches never receive literal {{...}} or empty labels (#199).
          const personalizedSubject = stripUnresolvedTags(resolveEmailVariables(subject.trim(), {
            athlete: athleteProfile,
            coach,
            sportInfo,
            academicData,
            performanceMetrics,
            schoolInfo,
            profileShareUrl,
            eventSchedule
          }));
          const personalizedBody = stripUnresolvedTags(resolveEmailVariables(emailBody.trim(), {
            athlete: athleteProfile,
            coach,
            sportInfo,
            academicData,
            performanceMetrics,
            schoolInfo,
            profileShareUrl,
            eventSchedule
          }));

          const parsedCc = ccEmails.split(',').map(e => e.trim()).filter(e => e.length > 0);
          const parsedBcc = bccEmails.split(',').map(e => e.trim()).filter(e => e.length > 0);

          await integrationsAPI.sendGmail(athleteId, {
            recipients: [{
              email: coach.email,
              coach_id: coach.coach_id,
              school_id: coach.school_id
            }],
            subject: personalizedSubject,
            body: personalizedBody,
            reply_to: undefined,
            cc_emails: parsedCc.length > 0 ? parsedCc : undefined,
            bcc_emails: parsedBcc.length > 0 ? parsedBcc : undefined,
            attachments: attachmentObjects.length > 0 ? attachmentObjects : undefined,
            display_name: athleteProfile
              ? `${athleteProfile.firstName || ''} ${athleteProfile.lastName || ''}`.trim()
              : athleteName,
          });

          newlySent.push(coach.email);
          setSentCount((n) => n + 1);

          // Rate limiting: wait 750ms between sends
          if (i < recipientsToSend.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 750));
          }
        } catch (error) {
          console.error(`Failed to send email to ${coach.coach_name}:`, error);
          failed.push({ name: coach.coach_name, school: coach.school });
        }
      }

      // Remember who succeeded so a retry skips them.
      if (newlySent.length > 0) {
        setSentCoachKeys(prev => {
          const next = new Set(prev);
          newlySent.forEach(email => next.add(email));
          return next;
        });
      }
      setFailedRecipients(failed);

      // Show results
      if (failed.length === 0) {
        // Count earlier sends too, so a retry names the whole result.
        const total = sentCoachKeys.size + newlySent.length;
        toast.success(`Sent to ${total} coach${total === 1 ? '' : 'es'}.`);
        handleClose();
        onSent?.();
      } else if (newlySent.length > 0) {
        toast.warning(`Sent to ${newlySent.length}. ${failed.length} failed. Retry sends only to the ${failed.length} that failed.`);
      } else {
        toast.error('Failed to send emails. Please try again.');
      }
    } catch (error) {
      console.error('Error sending bulk emails:', error);
      toast.error('Failed to send emails. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  // Route this composer's own X/Cancel through the Modal's guarded close so the
  // "Discard changes?" prompt fires from them too (not just backdrop/Escape).
  const composerModalRef = useRef<ModalHandle>(null);
  const handleClose = () => {
    setSubject('');
    setEmailBody('');
    setCcEmails('');
    setBccEmails('');
    setShowCcBcc(false);
    setAttachedMedia([]);
    setUploadProgress({});
    setIsUploadingAttachment(false);
    setSentCoachKeys(new Set());
    setFailedRecipients([]);
    onClose();
  };

  const handleYouTubeVideosSelected = (videoLinks: string[]) => {
    const currentBody = emailBody;
    const videosText = videoLinks.join('\n');
    const newBody = currentBody ? `${currentBody}\n\n${videosText}` : videosText;
    setEmailBody(newBody);
  };

  if (!isOpen) return null;

  // Coaches still needing an email (all, minus those already sent this session).
  const remainingCount = coaches.filter(c => !sentCoachKeys.has(c.email)).length;
  const hasSentSome = sentCoachKeys.size > 0;
  const sendDisabled = isSending || !subject.trim() || !emailBody.trim() || (hasSentSome && remainingCount === 0);

  return (
    <>
      <RemoveCoachConfirm />
      <style>
        {`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
        `}
      </style>
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        ref={composerModalRef}
        isDirty={subject.trim().length > 0 || emailBody.trim().length > 0}
        disableClose={isSending || youtubePickerVisible || templatePickerVisible}
        ariaLabel="Email Coaches"
        zIndex={1002}
        contentStyle={{
          width: isMobile ? 'calc(100vw - 32px)' : '100%',
          maxWidth: isMobile ? 'calc(100vw - 32px)' : '900px',
          maxHeight: isMobile ? 'calc(100vh - 100px)' : '90vh',
          background: 'white',
          borderRadius: '16px',
          overflow: 'hidden',
          padding: 0,
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
          {/* Header */}
          <div
            style={{
              backgroundColor: '#151133',
              borderTopLeftRadius: '16px',
              borderTopRightRadius: '16px',
              padding: isMobile ? '16px' : '20px 24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexShrink: 0,
            }}
          >
            <button
              onClick={() => (composerModalRef.current ? composerModalRef.current.requestClose() : handleClose())}
              disabled={isSending}
              style={{
                background: 'none',
                border: 'none',
                color: 'white',
                cursor: isSending ? 'not-allowed' : 'pointer',
                padding: '8px',
                display: 'flex',
                alignItems: 'center',
                opacity: isSending ? 0.5 : 1,
              }}
            >
              <X size={24} />
            </button>
            <h2
              style={{
                margin: 0,
                fontSize: '18px',
                fontWeight: '600',
                color: 'white',
              }}
            >
              Email Coaches
            </h2>
            <div style={{ width: '40px' }} />
          </div>

          {/* Content */}
          <div
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: '32px',
              background: '#F9FAFB',
            }}
          >
            {isCheckingConnection ? (
              /* Loading State */
              <div style={{
                backgroundColor: 'white',
                padding: '48px 32px',
                borderRadius: '12px',
                textAlign: 'center',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
              }}>
                <div style={{
                  width: '80px',
                  height: '80px',
                  backgroundColor: '#FEF2F2',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 24px',
                  animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
                }}>
                  <Mail size={48} color="#EA4335" />
                </div>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: '700',
                  color: '#1F2937',
                  margin: '0 0 12px'
                }}>
                  Initializing Email Composer...
                </h3>
              </div>
            ) : !isGmailConnected ? (
              /* Gmail Not Connected */
              <div style={{
                backgroundColor: 'white',
                padding: '48px 32px',
                borderRadius: '12px',
                textAlign: 'center',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
              }}>
                <div style={{
                  width: '80px',
                  height: '80px',
                  backgroundColor: '#FEF2F2',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 24px'
                }}>
                  <Mail size={48} color="#EA4335" />
                </div>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: '700',
                  color: '#1F2937',
                  margin: '0 0 12px'
                }}>
                  Gmail Account Not Connected
                </h3>
                <p style={{
                  fontSize: '14px',
                  color: '#6B7280',
                  lineHeight: '1.5',
                  margin: '0 0 16px'
                }}>
                  To send emails to coaches, you need to connect your Gmail account first.
                </p>
                <p style={{
                  fontSize: '14px',
                  color: '#F9550D',
                  lineHeight: '1.5',
                  margin: 0
                }}>
                  Go to Messages <ChevronRight size={13} style={{ display: 'inline', verticalAlign: 'middle' }} aria-hidden="true" /> Settings to connect your Gmail account.
                </p>
              </div>
            ) : (
              <>
                {/* Coaches List */}
                <div
                  style={{
                    background: 'white',
                    borderRadius: '12px',
                    marginBottom: '24px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      padding: '16px',
                      borderBottom: '1px solid #E5E7EB',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                    }}
                  >
                    <div
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '8px',
                        background: 'rgba(234, 67, 53, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Mail size={20} color="#EA4335" />
                    </div>
                    <div style={{ fontWeight: '600', color: '#1F2937' }}>
                      {coaches.length} Coach{coaches.length > 1 ? 'es' : ''} Selected
                    </div>
                  </div>

                  {coaches.map((coach, index) => (
                    <div
                      key={`${coach.email}-${index}`}
                      style={{
                        padding: '16px',
                        borderBottom: index < coaches.length - 1 ? '1px solid #F3F4F6' : 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                        {coach.schoolLogo ? (
                          <img
                            src={coach.schoolLogo}
                            alt={coach.school}
                            style={{
                              width: '32px',
                              height: '32px',
                              objectFit: 'contain',
                              borderRadius: '6px',
                            }}
                          />
                        ) : (
                          <div
                            style={{
                              width: '32px',
                              height: '32px',
                              borderRadius: '6px',
                              background: '#F3F4F6',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2">
                              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                              <circle cx="8.5" cy="8.5" r="1.5"></circle>
                              <polyline points="21 15 16 10 5 21"></polyline>
                            </svg>
                          </div>
                        )}
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '15px', fontWeight: '600', color: '#1F2937' }}>
                            {coach.coach_name}
                          </div>
                          {coach.title && (
                            <div style={{ fontSize: '13px', color: '#6B7280' }}>
                              {coach.title}
                            </div>
                          )}
                          {coach.school && (
                            <div style={{ fontSize: '13px', color: '#6B7280' }}>
                              {coach.school}
                            </div>
                          )}
                          <div style={{ fontSize: '13px', color: '#6B7280', marginTop: '2px' }}>
                            {coach.email}
                          </div>
                        </div>
                      </div>
                      {onRemoveCoach && coaches.length > 1 && (
                        <button
                          onClick={() => handleRemoveCoach(coach.email, coach.coach_name)}
                          style={{
                            padding: '6px',
                            background: 'transparent',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#EF4444',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#FEE2E2';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'transparent';
                          }}
                        >
                          <X size={14} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>

                {/* Partial-failure summary — which coaches still need sending (#191) */}
                {failedRecipients.length > 0 && (
                  <div style={{
                    background: '#FEF2F2',
                    border: '1px solid #FECACA',
                    borderRadius: '12px',
                    padding: '16px',
                    marginBottom: '24px',
                    display: 'flex',
                    gap: '12px',
                    alignItems: 'flex-start',
                  }}>
                    <AlertTriangle size={18} color="#DC2626" style={{ flexShrink: 0, marginTop: '2px' }} aria-hidden="true" />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '14px', fontWeight: 700, color: '#991B1B', marginBottom: '6px' }}>
                        Couldn't send to {failedRecipients.length} coach{failedRecipients.length === 1 ? '' : 'es'}
                      </div>
                      <div style={{ fontSize: '13px', color: '#991B1B', marginBottom: '8px', lineHeight: 1.4 }}>
                        These coaches were not emailed. Retry sends only to them.
                      </div>
                      <ul style={{ margin: 0, paddingLeft: '18px', color: '#991B1B', fontSize: '13px', lineHeight: 1.5 }}>
                        {failedRecipients.map((r, i) => (
                          <li key={i}>{r.name}{r.school ? ` — ${r.school}` : ''}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                {/* Email Form */}
                <div style={{
                  backgroundColor: 'white',
                  padding: '20px',
                  borderRadius: '12px',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
                }}>
                  {/* Compose / Preview Toggle */}
                  <div style={{
                    display: 'flex',
                    backgroundColor: '#F3F4F6',
                    borderRadius: '8px',
                    padding: '3px',
                    marginBottom: '20px',
                    gap: '4px'
                  }}>
                    <button
                      onClick={() => setPreviewMode(false)}
                      style={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '8px 16px',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        backgroundColor: !previewMode ? '#151133' : 'transparent',
                        color: !previewMode ? 'white' : '#6B7280',
                        transition: 'all 0.2s'
                      }}
                    >
                      <Edit3 size={14} style={{ marginRight: 6 }} /> Compose
                    </button>
                    <button
                      onClick={() => setPreviewMode(true)}
                      disabled={!subject.trim() && !emailBody.trim()}
                      style={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '8px 16px',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: (!subject.trim() && !emailBody.trim()) ? 'not-allowed' : 'pointer',
                        backgroundColor: previewMode ? '#151133' : 'transparent',
                        color: previewMode ? 'white' : '#6B7280',
                        opacity: (!subject.trim() && !emailBody.trim()) ? 0.4 : 1,
                        transition: 'all 0.2s'
                      }}
                    >
                      <Eye size={14} style={{ marginRight: 6 }} /> Preview
                    </button>
                  </div>

                  {previewMode ? (
                    /* Preview Mode */
                    <div>
                      {/* Coach Selector */}
                      {coaches.length > 1 && (
                        <div style={{ marginBottom: '16px' }}>
                          <label htmlFor="bulk-email-preview-coach" style={{ display: 'block', fontSize: '12px', color: '#9CA3AF', marginBottom: '4px', fontWeight: '500' }}>Previewing as</label>
                          {/* <Select> draws its own chevron, so `aps-chevron` is
                              gone — it would paint a second one — and the right
                              padding clears the one that is left. The heading
                              above it became a real <label>; it was a bare div,
                              so the picker announced itself unnamed. */}
                          <Select
                            id="bulk-email-preview-coach"
                            value={previewCoachIndex}
                            onChange={(e) => setPreviewCoachIndex(Number(e.target.value))}
                            style={{
                              padding: '10px 34px 10px 12px',
                              border: '1px solid #D1D5DB',
                              borderRadius: '8px',
                              fontSize: '14px',
                            }}
                          >
                            {coaches.map((c, idx) => (
                              <option key={idx} value={idx}>
                                {c.coach_name}{c.school ? ` — ${c.school}` : ''}
                              </option>
                            ))}
                          </Select>
                        </div>
                      )}
                      <div style={{ marginBottom: '16px' }}>
                        <div style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '4px', fontWeight: '500' }}>Subject</div>
                        <div style={{
                          fontSize: '15px',
                          fontWeight: '600',
                          color: '#1F2937',
                          padding: '12px 16px',
                          backgroundColor: '#F9FAFB',
                          borderRadius: '8px',
                          border: '1px solid #E5E7EB'
                        }}>
                          {/* Stripped as well as resolved: the preview's job is
                              to show what this coach receives, and the send
                              below removes every unfilled tag and the empty
                              "Label:" line it leaves. Previewing the unstripped
                              text showed a "Position:" the coach never gets. */}
                          {stripUnresolvedTags(resolveEmailVariables(subject, {
                            athlete: athleteProfile,
                            coach: coaches[previewCoachIndex],
                            sportInfo,
                            academicData,
                            performanceMetrics,
                            schoolInfo,
                            profileShareUrl,
                            eventSchedule
                          })) || <span style={{ color: '#9CA3AF', fontStyle: 'italic' }}>No subject</span>}
                        </div>
                      </div>
                      <div style={{ marginBottom: '16px' }}>
                        <div style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '4px', fontWeight: '500' }}>Message</div>
                        <div style={{
                          fontSize: '14px',
                          color: '#1F2937',
                          padding: '16px',
                          backgroundColor: '#F9FAFB',
                          borderRadius: '8px',
                          border: '1px solid #E5E7EB',
                          whiteSpace: 'pre-wrap',
                          lineHeight: '1.6',
                          minHeight: '200px'
                        }}>
                          {stripUnresolvedTags(resolveEmailVariables(emailBody, {
                            athlete: athleteProfile,
                            coach: coaches[previewCoachIndex],
                            sportInfo,
                            academicData,
                            performanceMetrics,
                            schoolInfo,
                            profileShareUrl,
                            eventSchedule
                          })) || <span style={{ color: '#9CA3AF', fontStyle: 'italic' }}>No message</span>}
                        </div>
                      </div>
                      {attachedMedia.length > 0 && (
                        <div style={{ marginBottom: '16px' }}>
                          <div style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '8px', fontWeight: '500' }}>Attachments ({attachedMedia.length})</div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                            {attachedMedia.map((file, idx) => (
                              <div key={idx} style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                padding: '6px 12px',
                                backgroundColor: '#F3F4F6',
                                borderRadius: '6px',
                                fontSize: '13px',
                                color: '#374151'
                              }}>
                                <Paperclip size={12} />
                                {file.name || `Attachment ${idx + 1}`}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    /* Compose Mode */
                    <>
                    {/* Template picker surfaced FIRST — starting from a template is
                        the intended path, and it used to be buried below the message
                        field so users hit Send without seeing it (KGH-785). */}
                    <button
                      onClick={() => setTemplatePickerVisible(true)}
                      disabled={isSending}
                      style={{
                        width: '100%',
                        marginBottom: '20px',
                        padding: '12px 16px',
                        backgroundColor: '#FFF7ED',
                        border: '2px solid #f9550d',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        cursor: isSending ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#f9550d',
                        opacity: isSending ? 0.5 : 1,
                        transition: 'background 0.2s'
                      }}
                      onMouseEnter={(e) => !isSending && (e.currentTarget.style.backgroundColor = '#FFEDD5')}
                      onMouseLeave={(e) => !isSending && (e.currentTarget.style.backgroundColor = '#FFF7ED')}
                    >
                      <File size={18} />
                      Start from a Template
                    </button>
                  {/* CC/BCC Toggle */}
                  <div style={{ marginBottom: '16px' }}>
                    {!showCcBcc ? (
                      <button
                        onClick={() => setShowCcBcc(true)}
                        disabled={isSending}
                        style={{
                          background: 'none',
                          border: 'none',
                          fontSize: '13px',
                          fontWeight: '500',
                          color: '#6B7280',
                          cursor: 'pointer',
                          padding: '4px 0',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.color = '#F9550D')}
                        onMouseLeave={(e) => (e.currentTarget.style.color = '#6B7280')}
                      >
                        + Add CC / BCC
                      </button>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <div>
                          <label htmlFor="bulk-email-cc" style={{
                            display: 'block', fontSize: '12px', fontWeight: '600',
                            color: '#6B7280', marginBottom: '4px',
                            textTransform: 'uppercase' as const, letterSpacing: '0.5px'
                          }}>
                            CC
                          </label>
                          <TextInput
                            id="bulk-email-cc"
                            type="text"
                            value={ccEmails}
                            onChange={(e) => setCcEmails(e.target.value)}
                            placeholder="email1@example.com, email2@example.com"
                            disabled={isSending}
                            style={{
                              padding: '10px 14px',
                              border: '1px solid #D1D5DB', borderRadius: '8px',
                              fontSize: '13px',
                            }}
                          />
                        </div>
                        <div>
                          <label htmlFor="bulk-email-bcc" style={{
                            display: 'block', fontSize: '12px', fontWeight: '600',
                            color: '#6B7280', marginBottom: '4px',
                            textTransform: 'uppercase' as const, letterSpacing: '0.5px'
                          }}>
                            BCC
                          </label>
                          <TextInput
                            id="bulk-email-bcc"
                            type="text"
                            value={bccEmails}
                            onChange={(e) => setBccEmails(e.target.value)}
                            placeholder="email1@example.com, email2@example.com"
                            disabled={isSending}
                            style={{
                              padding: '10px 14px',
                              border: '1px solid #D1D5DB', borderRadius: '8px',
                              fontSize: '13px',
                            }}
                          />
                        </div>
                        <button
                          onClick={() => { setShowCcBcc(false); setCcEmails(''); setBccEmails(''); }}
                          style={{
                            alignSelf: 'flex-start', background: 'none', border: 'none',
                            fontSize: '12px', color: '#9CA3AF', cursor: 'pointer', padding: '2px 0'
                          }}
                        >
                          Remove CC/BCC
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Subject */}
                  <div style={{ marginBottom: '20px' }}>
                    <label htmlFor="bulk-email-subject" style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#000000',
                      marginBottom: '8px'
                    }}>
                      Subject
                    </label>
                    <TextInput
                      id="bulk-email-subject"
                      type="text"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      placeholder="Email subject"
                      disabled={isSending}
                      style={{
                        padding: '12px 16px',
                        border: '1px solid #D1D5DB',
                        borderRadius: '8px',
                        fontSize: '14px',
                      }}
                    />
                  </div>

                  {/* Message */}
                  <div style={{ marginBottom: '20px' }}>
                    <label htmlFor="bulk-email-body" style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#000000',
                      marginBottom: '8px'
                    }}>
                      Message
                    </label>
                    <TextArea
                      id="bulk-email-body"
                      value={emailBody}
                      onChange={(e) => setEmailBody(e.target.value)}
                      placeholder="Write your message..."
                      disabled={isSending}
                      style={{
                        height: '200px',
                        padding: '12px 16px',
                        border: '1px solid #D1D5DB',
                        borderRadius: '8px',
                        fontSize: '14px',
                      }}
                    />

                    {/* Quick Tags */}
                    <QuickTags
                      tags={tags}
                      onInsertTag={insertQuickTag}
                      disabled={isSending}
                    />

                    {/* Hint: Profile Share Link tag only offered when sharing is on. */}
                    {!profileShareUrl && (
                      <p style={{
                        margin: '8px 0 0',
                        fontSize: '12px',
                        color: '#6B7280',
                        lineHeight: 1.4,
                      }}>
                        Tip: Turn on profile sharing in your profile's Share settings to add a
                        <strong> Profile Share Link</strong> tag to your emails.
                      </p>
                    )}

                    {/* YouTube Videos Button */}
                    <button
                      onClick={() => setYoutubePickerVisible(true)}
                      disabled={isSending}
                      style={{
                        width: '100%',
                        marginTop: '16px',
                        padding: '16px 20px',
                        backgroundColor: '#FFF5F5',
                        border: '2px solid #FF0000',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        cursor: isSending ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#FF0000',
                        opacity: isSending ? 0.5 : 1,
                        transition: 'background 0.2s'
                      }}
                      onMouseEnter={(e) => !isSending && (e.currentTarget.style.backgroundColor = '#FFE5E5')}
                      onMouseLeave={(e) => !isSending && (e.currentTarget.style.backgroundColor = '#FFF5F5')}
                    >
                      <PlayCircle size={20} />
                      YouTube Videos
                    </button>
                  </div>

                  {/* Attachments */}
                  <div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept="image/*,video/*,.pdf,.doc,.docx"
                      onChange={handleFileSelect}
                      style={{ display: 'none' }}
                    />
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isSending || isUploadingAttachment || attachedMedia.length >= 4}
                      style={{
                        width: '100%',
                        padding: '16px',
                        background: '#F8FAFC',
                        border: '2px dashed #F9550D',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#F9550D',
                        cursor: isSending || isUploadingAttachment || attachedMedia.length >= 4 ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        opacity: isSending || isUploadingAttachment || attachedMedia.length >= 4 ? 0.5 : 1,
                        transition: 'background 0.2s',
                        marginBottom: '15px'
                      }}
                      onMouseEnter={(e) => {
                        if (!isSending && !isUploadingAttachment && attachedMedia.length < 4) {
                          e.currentTarget.style.background = '#FFF5F0';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isSending && !isUploadingAttachment && attachedMedia.length < 4) {
                          e.currentTarget.style.background = '#F8FAFC';
                        }
                      }}
                    >
                      <Paperclip size={16} />
                      {isUploadingAttachment ? 'Uploading...' : 'Add File'}
                    </button>

                    {/* Attachment Guidelines — limits match handleFileSelect (#241) */}
                    <div style={{
                      fontSize: '12px',
                      color: '#6B7280',
                      textAlign: 'center',
                      lineHeight: '1.4',
                      marginBottom: '15px'
                    }}>
                      Max size: images 10MB, videos 100MB, documents 25MB.<br />
                      Supported formats: PDF, DOC, images, video, and more.
                    </div>

                    {/* Attached Files Preview */}
                    {attachedMedia.length > 0 && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {attachedMedia.map((file, index) => (
                          <div
                            key={index}
                            style={{
                              padding: '12px',
                              background: '#F9FAFB',
                              border: '1px solid #E5E7EB',
                              borderRadius: '8px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between'
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                              <File size={16} color="#6B7280" />
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ 
                                  fontSize: '14px', 
                                  color: '#1F2937', 
                                  overflow: 'hidden', 
                                  textOverflow: 'ellipsis', 
                                  whiteSpace: 'nowrap' 
                                }}>
                                  {file.name}
                                </div>
                                <div style={{ fontSize: '12px', color: '#6B7280' }}>
                                  {(file.size / 1024 / 1024).toFixed(2)} MB
                                </div>
                              </div>
                            </div>
                            <button
                              onClick={() => handleRemoveAttachment(index)}
                              disabled={isSending}
                              style={{
                                padding: '6px',
                                background: 'transparent',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: isSending ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                color: '#EF4444',
                                opacity: isSending ? 0.5 : 1
                              }}
                              onMouseEnter={(e) => !isSending && (e.currentTarget.style.background = '#FEE2E2')}
                              onMouseLeave={(e) => !isSending && (e.currentTarget.style.background = 'transparent')}
                            >
                              <X size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                    </>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          {!isCheckingConnection && isGmailConnected && (
            <div
              style={{
                padding: '20px 32px',
                borderTop: '1px solid #E5E7EB',
                display: 'flex',
                gap: '12px',
                flexShrink: 0,
                background: 'white',
              }}
            >
              <button
                onClick={() => (composerModalRef.current ? composerModalRef.current.requestClose() : handleClose())}
                disabled={isSending}
                style={{
                  flex: 1,
                  padding: '12px 24px',
                  background: 'white',
                  color: '#374151',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: isSending ? 'not-allowed' : 'pointer',
                  opacity: isSending ? 0.5 : 1,
                }}
              >
                Cancel
              </button>
              
              <button
                onClick={handleSendEmail}
                disabled={sendDisabled}
                style={{
                  flex: 1,
                  padding: '12px 24px',
                  background: sendDisabled ? '#E5E7EB' : '#F9550D',
                  color: sendDisabled ? '#9CA3AF' : 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: sendDisabled ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  opacity: sendDisabled ? 0.5 : 1,
                }}
              >
                <Mail size={16} />
                {isSending ? `Sending ${Math.min(sentCount + 1, remainingCount)} of ${remainingCount}...` :
                 isUploadingAttachment ? 'Uploading attachments...' :
                 hasSentSome ? `Retry ${remainingCount} failed coach${remainingCount === 1 ? '' : 'es'}` :
                 `Send ${coaches.length} Email${coaches.length === 1 ? '' : 's'} Now`}
              </button>
            </div>
          )}
        </Modal>

      {/* YouTube Video Picker Modal */}
      <YouTubeVideoPickerModal
        isOpen={youtubePickerVisible}
        onClose={() => setYoutubePickerVisible(false)}
        onSelectVideos={handleYouTubeVideosSelected}
        title="Select Videos"
      />

      {/* Template Picker Modal */}
      <TemplatePickerModal
        isOpen={templatePickerVisible}
        onClose={() => setTemplatePickerVisible(false)}
        onApply={(template) => {
          if (template.subject) setSubject(template.subject);
          setEmailBody(template.body);
        }}
      />
    </>
  );
};

export default BulkEmailCoachModal;
